<template>
  <nav id="sidebar" aria-label="Main Navigation">
    <!-- Side Header -->
    <div class="content-header bg-white-5">
      <!-- Logo -->

        <multiselect
          v-if="clients.length > 1"
                     style="max-width: 300px"
                     :allow-empty="false"
                     track-by="uuid"
                     class="organisation-select flex-grow-1"
                     :custom-label="(option) => option.name"
                     :placeholder="$t('search')"
                     v-model="currentClient"
                     :options="clients">
        </multiselect>
      <router-link v-if="clients.length === 1" class="font-w600 text-dual" :to="{name:'dashboard'}">
         {{ currentClient.name }}
      </router-link>
      <!-- END Logo -->

      <!-- Options -->
      <div>
        <!-- Close Sidebar, Visible only on mobile screens -->
        <!-- Layout API, functionality initialized in Template._uiApiLayout() -->
        <a class="d-lg-none text-dual ml-3" @click="closeNav()" href="javascript:void(0)">
          <i class="fa fa-times"></i>
        </a>
        <!-- END Close Sidebar -->
      </div>
      <!-- END Options -->
    </div>
    <!-- END Side Header -->

    <!-- Side Navigation -->
    <div class="content-side content-side-full">
      <ul class="nav-main">
        <li class="nav-main-heading pt-0">{{ $t('navbar.main') }}</li>
        <li class="nav-main-item">
          <router-link class="nav-main-link" :to="{name:'dashboard'}" @click.native="closeNav()">
            <i class="nav-main-link-icon si si-cursor"></i>
            <span class="nav-main-link-name">{{ $t('navbar.dashboard') }}</span>
          </router-link>
        </li>
        <li v-for="(link, n) in mainLinks" :key="link.route.name + n" class="nav-main-item">
          <router-link class="nav-main-link" :to="link.route" @click.native="closeNav()">
            <i v-if="link.icon" :class="link.icon" class="nav-main-link-icon"></i>
            <span class="nav-main-link-name">{{ $tc(link.title, 2) }}</span>
          </router-link>
        </li>
        <li v-if="managementLinks.length" class="nav-main-heading">{{ $t('navbar.management') }}</li>
        <li v-for="(link, n) in managementLinks" :key="link.route.name + n" class="nav-main-item">
          <router-link class="nav-main-link" :to="link.route" @click.native="closeNav()">
            <i v-if="link.icon" :class="link.icon" class="nav-main-link-icon"></i>
            <span class="nav-main-link-name">{{ $tc(link.title, link.translationNumber ?? 2) }}</span>
          </router-link>
          <a v-if="link.iconFunction" href="javascript:void(0)" @click="link.iconFunction" class="icon-link"><i class="fa fa-plus-circle"></i>
          </a>
        </li>
        <li v-if="featuresLinks.length" class="nav-main-heading">{{ $t('navbar.features') }}</li>
        <li v-for="(link, n) in featuresLinks" :key="link.route.name + n" class="nav-main-item" @click="!isLocked(link) && closeNav()">
          <component
            :is="isLocked(link) ? 'span' : 'router-link'"
            class="nav-main-link"
            :class="{ 'nav-main-link--locked': isLocked(link), 'nav-main-link--disabled': link.isDisabled }"
            v-bind="isLocked(link) ? {} : { to: link.route }"
            :data-feature-key="link.isPremium ? link.key : null"
            :data-feature-locked="link.isPremium ? isLocked(link) : null"
          >
            <div class="nav-main-link-icon-container">
              <i v-if="link.icon" :class="link.icon" class="nav-main-link-icon"></i>
              <premium-icon v-if="link.isPremium" class="premium-icon" />
            </div>
            <span class="nav-main-link-name">{{ $tc(link.title, link.translationNumber ?? 2) }}</span>
          </component>
          <a v-if="link.iconFunction && !isLocked(link)" href="javascript:void(0)" @click="link.iconFunction" class="icon-link"><i class="fa fa-plus-circle"></i>
          </a>
          <component
            v-if="link.isUnderConstruction"
            :is="isLocked(link) ? 'span' : 'router-link'"
            v-bind="isLocked(link) ? {} : { to: link.route }"
            class="under-construction-icon-container"
          >
            <under-construction-icon class="under-construction-icon" />
          </component>
        </li>
        <!--<li class="nav-main-item">-->
        <!--<a class="nav-main-link nav-main-link-submenu" data-toggle="submenu" aria-haspopup="true" aria-expanded="true" href="#">-->
        <!--<i class="nav-main-link-icon si si-bulb"></i>-->
        <!--<span class="nav-main-link-name">Examples</span>-->
        <!--</a>-->
        <!--<ul class="nav-main-submenu">-->
        <!--</ul>-->
        <!--</li>-->
        <li v-if="settingsLinks.length" class="nav-main-heading">{{ $t('navbar.settings') }}</li>
        <li v-for="(link, n) in settingsLinks" :key="link.route.name + n" class="nav-main-item">
          <router-link class="nav-main-link" :to="link.route" @click.native="closeNav()">
            <i v-if="link.icon" :class="link.icon" class="nav-main-link-icon"></i>
            <span class="nav-main-link-name">{{ $tc(link.title, link.translationNumber ?? 2) }}</span>
          </router-link>
        </li>
      </ul>
    </div>
    <!-- END Side Navigation -->
  </nav>
</template>

<script>
import router from '@/router'
import { useCreateNewEntity } from '@/use/useCreateNewEntity'
import Multiselect from 'vue-multiselect'
import { useGameSwap } from '@/use/useGameSwap'
import PremiumIcon from '@/components/svg/PremiumIcon.vue'
import UnderConstructionIcon from '@/components/svg/UnderConstruction.vue'
import tippy from 'tippy.js'

export default {
  components: { Multiselect, PremiumIcon, UnderConstructionIcon },
  setup () {
    const { createEmptySponsorAndEdit, createEmptyNewsItemAndEdit } = useCreateNewEntity()
    const { swapGame } = useGameSwap()
    return { createEmptySponsorAndEdit, createEmptyNewsItemAndEdit, swapGame }
  },
  computed: {
    mainLinks () {
      return [
        {
          title: 'schedule.name',
          icon: 'fa fa-calendar',
          route: { name: 'schedules.index', params: { clientId: this.currentClient.uuid } }
        }
      ]
    },
    managementLinks () {
      if (!this.$store.getters.isAdmin) {
        return []
      }

      return [
        {
          title: 'player.name',
          icon: 'fa fa-child',
          route: { name: 'players.index', params: { clientId: this.currentClient.uuid } },
          iconFunction: () => this.goToRoute({ name: 'players.create', params: { clientId: this.currentClient.uuid } })
        },
        {
          title: 'coach.name',
          icon: 'fa  fa-user-circle',
          route: { name: 'coaches.index', params: { clientId: this.currentClient.uuid } },
          iconFunction: () => this.goToRoute({ name: 'coaches.create', params: { clientId: this.currentClient.uuid } })
        },
        {
          title: 'club.name',
          icon: 'fa fa-map-signs',
          route: { name: 'clubs.index', params: { clientId: this.currentClient.uuid } },
          iconFunction: () => this.goToRoute({ name: 'clubs.create', params: { clientId: this.currentClient.uuid } })
        },
        {
          title: 'league.name',
          icon: 'fa fa-table',
          route: { name: 'leagues.index', params: { clientId: this.currentClient.uuid } },
          iconFunction: () => this.goToRoute({ name: 'leagues.create', params: { clientId: this.currentClient.uuid } })
        },
        {
          title: 'season.name',
          icon: 'fa fa-trophy',
          route: { name: 'seasons.index', params: { clientId: this.currentClient.uuid } },
          iconFunction: () => this.goToRoute({ name: 'seasons.create', params: { clientId: this.currentClient.uuid } })
        },
        {
          title: 'venue.name',
          icon: 'fa fa-map-marker',
          route: { name: 'venues.index', params: { clientId: this.currentClient.uuid } },
          iconFunction: () => this.goToRoute({ name: 'venues.create', params: { clientId: this.currentClient.uuid } })
        }
      ]
    },
    featuresLinks () {
      if (!this.$store.getters.isAdmin) {
        return []
      }

      const links = [
        {
          title: 'qr_code',
          translationNumber: 2,
          icon: 'fa fa-qrcode',
          route: { name: 'qrCode.index', params: { clientId: this.currentClient.uuid } },
          key: 'qr-code',
          isPremium: false
        },
        {
          title: 'applicationWindow.name',
          icon: 'fa fa-book',
          route: { name: 'applicationWindow.index', params: { clientId: this.currentClient.uuid } },
          iconFunction: () => this.goToRoute({ name: 'applicationWindow.create', params: { clientId: this.currentClient.uuid } }),
          key: 'application-windows',
          isPremium: true
        },
        {
          title: 'news.name',
          icon: 'fa fa-newspaper-o',
          route: { name: 'news.index', params: { clientId: this.currentClient.uuid } },
          iconFunction: () => this.createEmptyNewsItemAndEdit(),
          key: 'news',
          isPremium: false
        },
        {
          title: 'sponsor.name',
          icon: 'fa fa-handshake-o',
          route: { name: 'sponsors.index', params: { clientId: this.currentClient.uuid } },
          iconFunction: () => this.createEmptySponsorAndEdit(),
          key: 'sponsors',
          isPremium: true
        },
        {
          title: 'fantasy.name',
          translationNumber: 1,
          icon: 'fa fa-magic',
          route: { name: 'fantasy.index', params: { clientId: this.currentClient.uuid } },
          key: 'fantasy',
          isPremium: true
        },
        {
          title: 'gallery.name',
          translationNumber: 1,
          icon: 'fa fa-photo',
          route: { name: 'gallery.index', params: { clientId: this.currentClient.uuid } },
          key: 'gallery',
          isPremium: false
        },
        {
          title: 'imageRequest.player',
          icon: 'si si-picture',
          route: { name: 'imageRequests.index', params: { clientId: this.currentClient.uuid } }
        },
        {
          title: 'livestream.name',
          icon: 'fa fa-video-camera',
          translationNumber: 1,
          route: { name: 'livestream.index', params: { clientId: this.currentClient.uuid } },
          key: 'livestream',
          isPremium: true
        },
        {
          title: 'tvApp.name',
          icon: 'fa fa-tv',
          route: { name: 'tvApp.index', params: { clientId: this.currentClient.uuid } },
          key: 'tv-app',
          isPremium: true
        },
        {
          title: 'votingForm.name',
          translationNumber: 2,
          icon: 'fa fa-check-square-o',
          route: { name: 'votingForms.index', params: { clientId: this.currentClient.uuid } },
          iconFunction: () => this.goToRoute({ name: 'votingForms.create', params: { clientId: this.currentClient.uuid } }),
          key: 'voting-forms',
          isPremium: true
        }
      ]

      return links
    },
    settingsLinks () {
      if (!this.$store.getters.isAdmin) {
        return []
      }

      return [
        {
          title: 'navbar.configuration',
          translationNumber: 2,
          icon: 'fa fa-cog',
          route: { name: 'settings.index', params: { clientId: this.currentClient.uuid } }
        },
        {
          title: 'user.name',
          translationNumber: 2,
          icon: 'fa fa-users',
          route: { name: 'members.index', params: { clientId: this.currentClient.uuid } }
        },
        {
          title: 'billing.name',
          translationNumber: 1,
          icon: 'fa fa-dollar',
          route: { name: 'billing.index', params: { clientId: this.currentClient.uuid } }
        },
        {
          title: 'navbar.productEnablement',
          icon: 'fa fa-toggle-on',
          route: { name: 'productEnablement.index', params: { clientId: this.currentClient.uuid } }
        }
      ]
    },
    currentClient: {
      get () {
        return this.$store.state.currentClient
      },
      async set (value) {
        await this.$store.dispatch('setClient', value)
        this.swapGame = null
        console.log('currentClient', this.currentClient.uuid)
        this.$router.push('/')
      }
    },
    clients () {
      return this.$store.state.user.clients
    }
  },
  mounted () {
    this.initFeatureTooltips()
  },
  watch: {
    '$store.state.disabledFeatures' () {
      this.$nextTick(this.initFeatureTooltips)
    },
    '$store.state.language' () {
      this.$nextTick(this.initFeatureTooltips)
    },
    '$store.state.isNavOpened' (val) {
      if (!val) this.hideAllFeatureTippys()
    }
  },
  methods: {
    isLocked (link) {
      return link.isDisabled || (link.key && this.$store.getters.disabledFeatures.includes(link.key))
    },
    initFeatureTooltips () {
      const tippyOptions = (content) => ({
        content,
        placement: 'top',
        theme: 'nav-tooltip',
        delay: 0,
        animation: false,
        zIndex: 99999999,
        appendTo: document.body,
        onShow (instance) {
          instance.popper.style.pointerEvents = 'all'
          const box = instance.popper.querySelector('.tippy-box')
          if (box) box.style.pointerEvents = 'all'
        },
        onCreate (instance) {
          instance.popper.classList.add('feature-locked-tippy')
          instance.popper.addEventListener('touchstart', (e) => {
            e.stopPropagation()
          }, { passive: true })
          instance.popper.addEventListener('touchend', (e) => {
            e.preventDefault()
            e.stopPropagation()
            instance.hide()
          }, { passive: false })
          instance.popper.addEventListener('click', (e) => {
            e.stopPropagation()
            instance.hide()
          })
        }
      })

      const featureEls = this.$el.querySelectorAll('[data-feature-key]')
      featureEls.forEach(el => {
        if (el._tippy) el._tippy.destroy()
        const locked = el.dataset.featureLocked === 'true'

        if (locked) {
          const isMobile = window.matchMedia('(max-width: 991px)').matches
          const navEl = this.$el
          tippy(el, {
            ...tippyOptions(this.$t('featureGate.not_available')),
            trigger: isMobile ? 'click' : 'mouseenter',
            hideOnClick: isMobile,
            onShow (instance) {
              if (!isMobile) return
              navEl.querySelectorAll('.nav-main-item').forEach(item => {
                if (!item.contains(instance.reference)) {
                  item.style.pointerEvents = 'none'
                }
              })
            },
            onHide () {
              if (!isMobile) return
              navEl.querySelectorAll('.nav-main-item').forEach(item => {
                item.style.pointerEvents = ''
              })
            }
          })
        } else {
          const premiumIcon = el.querySelector('.premium-icon')
          if (premiumIcon) {
            if (premiumIcon._tippy) premiumIcon._tippy.destroy()
            tippy(premiumIcon, tippyOptions(this.$t('featureGate.available')))
          }
        }
      })

      const underConstructionEls = this.$el.querySelectorAll('.under-construction-icon')
      underConstructionEls.forEach(el => {
        if (el._tippy) el._tippy.destroy()
        tippy(el, tippyOptions(this.$t('featureGate.under_construction')))
      })
    },
    goToRoute (route) {
      router.push(route)
    },
    hideAllFeatureTippys () {
      this.$el.querySelectorAll('[data-feature-key], .premium-icon').forEach(el => {
        if (el._tippy) el._tippy.hide()
      })
    },
    closeNav () {
      this.$store.dispatch('closeNav')
    }
  }
}
</script>
